import { createFileRoute } from "@tanstack/react-router";
import type { RemittanceDefaults } from "@somasi/shared";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/integrations/aws/resources";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Truncated } from "@/components/truncated";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Landmark, Star } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";
import { ExportMenu } from "./client-groups";

export const Route = createFileRoute("/_authenticated/bank-accounts")({
  head: () => ({
    meta: [
      { title: "Bank Accounts — Somasi Ops" },
      {
        name: "description",
        content: "Somasi bank accounts used for client payment instructions on invoices.",
      },
      { property: "og:title", content: "Bank Accounts — Somasi Ops" },
      {
        property: "og:description",
        content: "Somasi bank accounts used for client payment instructions on invoices.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BankAccountsPage,
});

type BankAccount = {
  id: string;
  account_name: string;
  bank_name: string;
  branch_address: string | null;
  account_number: string | null;
  iban: string | null;
  swift_code: string | null;
  intermediary_bank: string | null;
  intermediary_swift: string | null;
  intermediary_bank_address: string | null;
  beneficiary_address: string | null;
  notes: string | null;
  is_default: boolean;
  active: boolean;
};

const EMPTY = {
  account_name: "",
  bank_name: "",
  branch_address: "",
  account_number: "",
  iban: "",
  swift_code: "",
  intermediary_bank: "",
  intermediary_swift: "",
  intermediary_bank_address: "",
  beneficiary_address: "",
  notes: "",
  is_default: false,
  active: true,
};

function BankAccountsPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const sort = useListSort({ column: "account_name", ascending: true });
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());

  const filters = { query, activeFilter, sort: sort.sort };
  // Six data columns, plus the checkbox column for an operator who can bulk-edit.
  const columnCount = canEdit ? 7 : 6;
  const pager = usePagination({ resetKey: filters });

  /** The list the filters describe — the table pages it, the export takes it whole. */
  const filteredList = () => {
    const list = api.bankAccounts.list();

    if (activeFilter !== "all") list.eq("active", activeFilter === "active");
    if (query) {
      // The identifiers are what someone reconciling a payment has in hand, so
      // they are searchable alongside the names.
      list.or([
        { column: "account_name", op: "ilike", value: `%${query}%` },
        { column: "bank_name", op: "ilike", value: `%${query}%` },
        { column: "account_number", op: "ilike", value: `%${query}%` },
        { column: "iban", op: "ilike", value: `%${query}%` },
        { column: "swift_code", op: "ilike", value: `%${query}%` },
      ]);
    }

    return list.order(sort.sort.column, { ascending: sort.sort.ascending });
  };

  // The filters, the sort and the page all go to the API, and all of them are
  // part of the cache key, so each control is a fetch rather than a re-filter in
  // the browser.
  const { data: result, isLoading } = useQuery({
    queryKey: ["bank_accounts", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as BankAccount[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const data = result?.rows ?? [];
  const total = result?.total;

  // The beneficiary block is the same on every account, so it is one row of its
  // own rather than a column repeated down the table. It is read here to prefill
  // a new account's form and to say, on the page, what the invoices will print.
  const { data: defaults } = useQuery({
    queryKey: ["remittance_defaults"],
    queryFn: async () => {
      const { data: rows, error } = await api.remittanceDefaults.list().all();
      if (error) throw error;
      return ((rows ?? []) as RemittanceDefaults[])[0] ?? null;
    },
  });

  const sel = useRowSelection(data);
  const refresh = () => qc.invalidateQueries({ queryKey: ["bank_accounts"] });

  const remove = async (row: BankAccount) => {
    if (!(await confirmDelete(`“${row.account_name} — ${row.bank_name}”`))) return;
    const { error } = await api.bankAccounts.remove(row.id);
    if (error) return notify.error(error);
    notify.success("Bank account deleted");
    refresh();
  };

  // One call: the resource demotes whichever account held the default, so the
  // two accounts can no longer both end up marked if the second write fails.
  const makeDefault = async (row: BankAccount) => {
    const { error } = await api.bankAccounts.update(row.id, { is_default: true });
    if (error) return notify.error(error);
    notify.success(`${row.account_name} is now the default account`);
    refresh();
  };

  // Every field the account form holds, in the order the form asks for them, so
  // the sheet reads as the wire does. The beneficiary address falls back to the
  // shared block the same way an invoice prints it — a null there means "use the
  // shared one", not "blank".
  const exportRows = async () => {
    const { data: all, error } = await filteredList().all();
    if (error) throw new Error(error.message);
    return ((all ?? []) as BankAccount[]).map((r) => ({
      "Beneficiary name (59)": r.account_name,
      "Beneficiary bank (57A)": r.bank_name,
      "Beneficiary bank address": r.branch_address ?? "",
      "Beneficiary address (59)": r.beneficiary_address ?? defaults?.beneficiary_address ?? "",
      "Default account": r.is_default ? "Yes" : "No",
      Active: r.active ? "Yes" : "No",
      "Account number": r.account_number ?? "",
      IBAN: r.iban ?? "",
      "SWIFT / BIC": r.swift_code ?? "",
      "Intermediary bank (56A)": r.intermediary_bank ?? "",
      "Intermediary SWIFT": r.intermediary_swift ?? "",
      "Intermediary bank address": r.intermediary_bank_address ?? "",
      Notes: r.notes ?? "",
    }));
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Bank Accounts"
          description="Somasi's receiving accounts. Pick one on an invoice and its payment instructions print on the document."
          actions={
            <>
              <ExportMenu
                filenameBase="somasi-bank-accounts"
                rows={exportRows}
                title="Bank Accounts"
                // The spreadsheets carry every field; the PDF is a printed page, so
                // it keeps the columns a payment is made from and leaves the
                // addresses and notes to the sheet.
                pdfCols={[
                  { header: "Beneficiary name", dataKey: "Beneficiary name (59)" },
                  { header: "Beneficiary bank", dataKey: "Beneficiary bank (57A)" },
                  { header: "Account number", dataKey: "Account number" },
                  { header: "IBAN", dataKey: "IBAN" },
                  { header: "SWIFT / BIC", dataKey: "SWIFT / BIC" },
                  { header: "Intermediary bank", dataKey: "Intermediary bank (56A)" },
                  { header: "Default", dataKey: "Default account" },
                  { header: "Active", dataKey: "Active" },
                ]}
              />
              {canEdit && (
                <Button variant="outline" onClick={() => setDefaultsOpen(true)}>
                  <Landmark className="mr-2 h-4 w-4" />
                  {defaults ? "Beneficiary details" : "Add beneficiary details"}
                </Button>
              )}
              {canEdit && (
                <Button
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> New account
                </Button>
              )}
            </>
          }
        />

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search account, bank, number, IBAN, SWIFT…"
          />
          <FilterSelect
            value={activeFilter}
            onChange={setActiveFilter}
            className="w-36"
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "all", label: "All" },
            ]}
          />
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.bankAccounts}
            entityLabel="bank account"
            activeColumn="active"
            onDone={refresh}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="account_name">
                Account name
              </SortHeader>
              <SortHeader sort={sort} column="bank_name">
                Bank
              </SortHeader>
              <TableHead>Account no.</TableHead>
              <TableHead>SWIFT / IBAN</TableHead>
              <SortHeader sort={sort} column="active">
                Status
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">
                {canEdit && "Actions"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : data.length === 0 ? (
              <TableMessageRow colSpan={columnCount}>
                <Landmark className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {query || activeFilter !== "all"
                  ? "No bank accounts match these filters."
                  : "No bank accounts yet."}
              </TableMessageRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id} data-state={sel.isSelected(row.id) ? "selected" : undefined}>
                  {canEdit && (
                    <TableCell>
                      <RowCheckbox sel={sel} id={row.id} />
                    </TableCell>
                  )}
                  <TableCell className="max-w-[16rem] font-medium">
                    <div className="flex items-center gap-2">
                      <Truncated text={row.account_name} />
                      {row.is_default && (
                        <Badge className="shrink-0 bg-primary text-primary-foreground">
                          Default
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[16rem]">
                    <Truncated text={row.bank_name} />
                    {row.branch_address && (
                      <Truncated
                        text={row.branch_address}
                        className="text-xs text-muted-foreground"
                      />
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.account_number ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{row.swift_code ?? "—"}</div>
                    {row.iban && <div className="text-muted-foreground">{row.iban}</div>}
                  </TableCell>
                  <TableCell>
                    {row.active ? (
                      <Badge className="bg-success text-success-foreground hover:bg-success/90">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <RowActions>
                        <RowAction
                          label="Edit"
                          onClick={() => {
                            setEditing(row);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil />
                        </RowAction>
                        {!row.is_default && (
                          <RowAction label="Set as default" onClick={() => makeDefault(row)}>
                            <Star />
                          </RowAction>
                        )}
                        <RowAction label="Delete" destructive onClick={() => remove(row)}>
                          <Trash2 />
                        </RowAction>
                      </RowActions>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="account" />
      </ListPageTable>

      <BankDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        defaults={defaults ?? null}
        onSaved={refresh}
      />

      <BeneficiaryDialog
        open={defaultsOpen}
        onOpenChange={setDefaultsOpen}
        current={defaults ?? null}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["remittance_defaults"] });
          refresh();
        }}
      />
    </ListPage>
  );
}

function BankDialog({
  open,
  onOpenChange,
  editing,
  defaults,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: BankAccount | null;
  defaults: RemittanceDefaults | null;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              account_name: editing.account_name,
              bank_name: editing.bank_name,
              branch_address: editing.branch_address ?? "",
              account_number: editing.account_number ?? "",
              iban: editing.iban ?? "",
              swift_code: editing.swift_code ?? "",
              intermediary_bank: editing.intermediary_bank ?? "",
              intermediary_swift: editing.intermediary_swift ?? "",
              intermediary_bank_address: editing.intermediary_bank_address ?? "",
              beneficiary_address:
                editing.beneficiary_address ?? defaults?.beneficiary_address ?? "",
              notes: editing.notes ?? "",
              is_default: editing.is_default,
              active: editing.active,
            }
          : {
              ...EMPTY,
              // A new account is Somasi's own until someone says otherwise, so
              // the 59 leg starts filled in rather than empty — and stays
              // editable, because an account held in another name has another
              // name and another address.
              account_name: defaults?.beneficiary_name ?? "",
              beneficiary_address: defaults?.beneficiary_address ?? "",
            },
      );
    }
  }, [open, editing, defaults]);

  const set = (k: keyof typeof EMPTY, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.account_name.trim() || !form.bank_name.trim())
      return notify.error("Account name and bank name are required");
    setSaving(true);
    const payload = {
      account_name: form.account_name.trim(),
      bank_name: form.bank_name.trim(),
      branch_address: form.branch_address || null,
      account_number: form.account_number || null,
      iban: form.iban || null,
      swift_code: form.swift_code || null,
      intermediary_bank: form.intermediary_bank || null,
      intermediary_swift: form.intermediary_swift || null,
      intermediary_bank_address: form.intermediary_bank_address || null,
      beneficiary_address:
        form.beneficiary_address && form.beneficiary_address !== defaults?.beneficiary_address
          ? form.beneficiary_address
          : null,
      notes: form.notes || null,
      is_default: form.is_default,
      active: form.active,
    };
    const { error } = editing
      ? await api.bankAccounts.update(editing.id, payload)
      : await api.bankAccounts.create(payload);
    setSaving(false);
    if (error) return notify.error(error);
    notify.success(editing ? "Bank account updated" : "Bank account added");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit bank account" : "New bank account"}</DialogTitle>
          <DialogDescription>
            These details are printed on invoices as payment instructions.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label required>Beneficiary name (59)</Label>
              <Input
                value={form.account_name}
                onChange={(e) => set("account_name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label required>Beneficiary bank (57A)</Label>
              <Input
                value={form.bank_name}
                onChange={(e) => set("bank_name", e.target.value)}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Beneficiary bank address</Label>
            <Input
              value={form.branch_address}
              onChange={(e) => set("branch_address", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Beneficiary address (59)</Label>
            <Input
              value={form.beneficiary_address}
              onChange={(e) => set("beneficiary_address", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Left matching the shared beneficiary details, editing those updates this account too.
              Change it here and this account keeps its own address.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Account number</Label>
              <Input
                value={form.account_number}
                onChange={(e) => set("account_number", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input value={form.iban} onChange={(e) => set("iban", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>SWIFT / BIC</Label>
              <Input value={form.swift_code} onChange={(e) => set("swift_code", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Intermediary bank (56A)</Label>
              <Input
                value={form.intermediary_bank}
                onChange={(e) => set("intermediary_bank", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Intermediary SWIFT</Label>
              <Input
                value={form.intermediary_swift}
                onChange={(e) => set("intermediary_swift", e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Intermediary bank address</Label>
            <Input
              value={form.intermediary_bank_address}
              onChange={(e) => set("intermediary_bank_address", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label>Default account</Label>
              <p className="text-xs text-muted-foreground">
                Pre-selected when creating a new invoice.
              </p>
            </div>
            <Switch checked={form.is_default} onCheckedChange={(v) => set("is_default", v)} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <Label>Active</Label>
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The beneficiary block, edited once for every account.
 *
 * `current` is null until it has been written for the first time, which is the
 * difference between the create and the patch — there is only ever one row, and
 * the server refuses a second, so the dialog does not offer to add another.
 */
function BeneficiaryDialog({
  open,
  onOpenChange,
  current,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  current: RemittanceDefaults | null;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    beneficiary_name: "",
    beneficiary_address: "",
    remittance_instruction: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      beneficiary_name: current?.beneficiary_name ?? "",
      beneficiary_address: current?.beneficiary_address ?? "",
      remittance_instruction: current?.remittance_instruction ?? "",
    });
  }, [open, current]);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.beneficiary_name.trim() || !form.beneficiary_address.trim())
      return notify.error("Beneficiary name and address are required");

    setSaving(true);
    const payload = {
      beneficiary_name: form.beneficiary_name.trim(),
      beneficiary_address: form.beneficiary_address.trim(),
      remittance_instruction: form.remittance_instruction.trim() || null,
    };
    const { error } = current
      ? await api.remittanceDefaults.update(current.id, payload)
      : await api.remittanceDefaults.create(payload);
    setSaving(false);
    if (error) return notify.error(error);
    notify.success("Beneficiary details saved");
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{current ? "Beneficiary details" : "Add beneficiary details"}</DialogTitle>
          <DialogDescription>
            The 59 leg of a wire — who the money is for. Shared by every bank account, and prefilled
            into a new one, since the accounts are all Somasi's own.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label required>Beneficiary name</Label>
            <Input
              value={form.beneficiary_name}
              onChange={(e) => set("beneficiary_name", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label required>Beneficiary address</Label>
            <Input
              value={form.beneficiary_address}
              onChange={(e) => set("beneficiary_address", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Remittance instruction</Label>
            <Textarea
              rows={2}
              value={form.remittance_instruction}
              onChange={(e) => set("remittance_instruction", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Printed in bold under the payment instructions on every invoice — what senders are
              asked to quote so the funds are applied to the right account.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
